import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  Collapse
} from '@mui/material';
import {
  TrendingUp as ChartIcon,
  CalendarToday as CalendarIcon,
  Euro as EuroIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { api } from '../contexts/ApiContext';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  equalFitCols,
  fitChipSx,
} from '../utils/fitTable';

interface Loan {
  id: number;
  loan_name: string;
  lender: string;
  original_amount: number;
  current_balance: number;
  interest_rate: number;
  monthly_payment: number;
  start_date: string;
  end_date: string;
  loan_type: string;
  payment_day?: number;
  is_tracker?: boolean;
  marginal_rate?: number;
}

interface AmortizationRow {
  month: number;
  date: string;
  payment: number;
  principal: number;
  interest: number;
  balance: number;
  isHistorical: boolean;
  interest_rate?: number; // Monthly interest rate for display
}

interface ECBRate {
  id: number;
  rate_date: string;
  main_refinancing_rate: number;
  deposit_facility_rate?: number;
  marginal_lending_rate?: number;
}

export const LoansSchedule: React.FC = () => {
  const [loans, setLoans] = useState<Loan[]>([]);
  const [selectedLoanId, setSelectedLoanId] = useState<number | null>(null);
  const [schedule, setSchedule] = useState<AmortizationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showHistoricalMonths, setShowHistoricalMonths] = useState(false);
  const [ecbRates, setEcbRates] = useState<Map<string, ECBRate>>(new Map()); // Map of date string to ECB rate

  useEffect(() => {
    fetchLoans();
  }, []);

  useEffect(() => {
    if (selectedLoanId !== null) {
      const loan = loans.find(l => l.id === selectedLoanId);
      if (loan) {
        // Fetch ECB rates if needed, then generate schedule
        if (loan.is_tracker) {
          fetchECBRatesForLoan(loan).then(() => {
            generateAmortizationSchedule(loan);
          });
        } else {
          generateAmortizationSchedule(loan);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLoanId, loans]);
  
  // Separate effect to regenerate schedule when ECB rates are loaded
  useEffect(() => {
    if (selectedLoanId !== null && ecbRates.size > 0) {
      const loan = loans.find(l => l.id === selectedLoanId);
      if (loan && loan.is_tracker) {
        generateAmortizationSchedule(loan);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ecbRates]);

  const fetchLoans = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/loans/');
      
      if (response.data && response.data.loans) {
        // Filter for mortgage and loan types
        const loanData = response.data.loans.filter((l: Loan) => 
          l.loan_type === 'mortgage' || l.loan_type === 'business' || l.loan_type === 'personal'
        );
        
        // Sort loans: mortgages first, then other loans
        // Within each group, sort by lender name
        loanData.sort((a: Loan, b: Loan) => {
          // Mortgages first
          if (a.loan_type === 'mortgage' && b.loan_type !== 'mortgage') return -1;
          if (b.loan_type === 'mortgage' && a.loan_type !== 'mortgage') return 1;
          
          // Within same type, sort by lender name
          return a.lender.localeCompare(b.lender);
        });
        
        setLoans(loanData);
        
        // Auto-select BOI mortgage if available, otherwise first loan
        const boiMortgage = loanData.find((l: Loan) => 
          l.loan_type === 'mortgage' && l.lender.toLowerCase().includes('boi')
        );
        
        if (boiMortgage) {
          setSelectedLoanId(boiMortgage.id);
        } else if (loanData.length > 0) {
          setSelectedLoanId(loanData[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching loans:', err);
      setError('Failed to load loans');
    } finally {
      setLoading(false);
    }
  };

  const fetchECBRatesForLoan = async (loan: Loan): Promise<void> => {
    try {
      const startDate = new Date(loan.start_date);
      const endDate = new Date(loan.end_date);
      
      // Fetch ECB rates for the loan period
      const response = await api.get('/api/ecb-rates/', {
        params: {
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0]
        }
      });
      
      if (response.data && response.data.ecb_rates) {
        const ratesMap = new Map<string, ECBRate>();
        response.data.ecb_rates.forEach((rate: ECBRate) => {
          ratesMap.set(rate.rate_date, rate);
        });
        setEcbRates(ratesMap);
      }
    } catch (err) {
      console.warn('Error fetching ECB rates (using fallback):', err);
      // Continue with empty ECB rates - will use fallback logic
    }
  };

  const getECBRateForDate = (date: Date): number | null => {
    if (ecbRates.size === 0) {
      return null;
    }
    
    // Sort ECB rates by date (descending) and find first one <= target date
    const sortedRates = Array.from(ecbRates.values())
      .map(rate => ({
        ...rate,
        date: new Date(rate.rate_date + 'T00:00:00') // Ensure consistent date parsing
      }))
      .sort((a, b) => b.date.getTime() - a.date.getTime());
    
    // Find the most recent rate on or before the target date
    for (const rate of sortedRates) {
      if (rate.date <= date) {
        return rate.main_refinancing_rate;
      }
    }
    
    return null;
  };

  const generateAmortizationSchedule = (loan: Loan) => {
    const rows: AmortizationRow[] = [];
    const startDate = new Date(loan.start_date);
    const endDate = new Date(loan.end_date);
    const today = new Date();
    
    // Calculate number of months
    const monthsDiff = Math.ceil(
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 30.44)
    );
    
    const principal = loan.original_amount;
    let balance = principal;
    
    // For tracker loans, we need to calculate payment using average rate or first rate
    // For non-tracker loans, use the fixed rate
    let baseAnnualRate = loan.interest_rate;
    if (loan.is_tracker && loan.marginal_rate !== undefined) {
      // For tracker, use current ECB rate + marginal as estimate for payment calculation
      const currentECB = getECBRateForDate(today);
      if (currentECB !== null) {
        baseAnnualRate = currentECB + loan.marginal_rate;
      } else {
        // Fallback: use the stored interest_rate (should be current rate)
        baseAnnualRate = loan.interest_rate;
      }
    }
    
    const baseMonthlyRate = baseAnnualRate / 100 / 12;
    
    // Calculate approximate monthly payment (will be adjusted per month for tracker loans)
    const monthlyPayment = baseMonthlyRate > 0 
      ? principal * (baseMonthlyRate * Math.pow(1 + baseMonthlyRate, monthsDiff)) / 
        (Math.pow(1 + baseMonthlyRate, monthsDiff) - 1)
      : principal / monthsDiff;
    
    for (let month = 1; month <= monthsDiff; month++) {
      const currentDate = new Date(startDate);
      currentDate.setMonth(startDate.getMonth() + month - 1);
      
      // Set the payment day if specified
      if (loan.payment_day) {
        currentDate.setDate(loan.payment_day);
      }
      
      // Calculate interest rate for this month
      let monthlyRate: number;
      let annualRate: number;
      
      if (loan.is_tracker && loan.marginal_rate !== undefined) {
        // Tracker loan: ECB rate + marginal rate
        const ecbRate = getECBRateForDate(currentDate);
        if (ecbRate !== null) {
          annualRate = ecbRate + loan.marginal_rate;
          monthlyRate = annualRate / 100 / 12;
        } else {
          // Fallback: use stored interest_rate if ECB rate not available
          annualRate = loan.interest_rate;
          monthlyRate = annualRate / 100 / 12;
        }
      } else {
        // Fixed rate loan
        annualRate = loan.interest_rate;
        monthlyRate = annualRate / 100 / 12;
      }
      
      const interestPayment = balance * monthlyRate;
      // For tracker loans, adjust payment based on actual rate
      // For fixed rate, use calculated payment
      let payment = monthlyPayment;
      if (loan.is_tracker && baseMonthlyRate !== monthlyRate) {
        // Recalculate payment for this rate (simplified - actual tracker loans may have fixed payments)
        // For now, we'll keep the original payment calculation but adjust interest
        payment = monthlyPayment; // Keep same payment, interest varies
      }
      
      const principalPayment = Math.min(payment - interestPayment, balance);
      balance = Math.max(0, balance - principalPayment);
      
      const isHistorical = currentDate <= today;
      
      rows.push({
        month,
        date: currentDate.toLocaleDateString('en-IE', { 
          month: 'short', 
          year: 'numeric',
          day: 'numeric'
        }),
        payment,
        principal: principalPayment,
        interest: interestPayment,
        balance,
        isHistorical,
        interest_rate: annualRate // Store annual rate for display
      });
      
      if (balance <= 0) break;
    }
    
    setSchedule(rows);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (rate: number | undefined): string => {
    if (rate === undefined || rate === null) return 'N/A';
    return `${rate.toFixed(2)}%`;
  };

  const getOrdinalSuffix = (day: number) => {
    if (day >= 11 && day <= 13) {
      return 'th';
    }
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const selectedLoan = loans.find(l => l.id === selectedLoanId);
  
  // Calculate totals
  const totalInterest = schedule.reduce((sum, row) => sum + row.interest, 0);
  const totalPrincipal = schedule.reduce((sum, row) => sum + row.principal, 0);
  const historicalMonths = schedule.filter(row => row.isHistorical).length;
  
  // Prepare chart data - show annual totals for better visualization
  const chartData = [];
  if (selectedLoan && schedule.length > 0) {
    const startYear = new Date(selectedLoan.start_date).getFullYear();
    const endYear = new Date(selectedLoan.end_date).getFullYear();
    
    console.log('Generating chart data:', { startYear, endYear, scheduleLength: schedule.length });
    
    for (let year = startYear; year <= endYear; year++) {
      const yearData = schedule.filter(row => {
        // Parse date from format like "1 Jan 2020" (en-IE format)
        const dateParts = row.date.split(' ');
        const yearStr = dateParts[dateParts.length - 1]; // Last part is the year
        const rowYear = parseInt(yearStr);
        return rowYear === year;
      });
      
      if (yearData.length > 0) {
        const totalCapital = yearData.reduce((sum, row) => sum + row.principal, 0);
        const totalInterest = yearData.reduce((sum, row) => sum + row.interest, 0);
        
        chartData.push({
          year: year.toString(),
          Capital: Math.round(totalCapital),
          Interest: Math.round(totalInterest),
          Total: Math.round(totalCapital + totalInterest)
        });
      }
    }
    
    console.log('Chart data generated:', chartData);
  }

  // Filter schedule to show latest 3 historical + all future when collapsed
  const getVisibleSchedule = () => {
    if (showHistoricalMonths) {
      return schedule;
    }
    
    // Find the boundary between historical and future payments
    const currentDate = new Date();
    const historicalPayments = schedule.filter(row => {
      const paymentDate = new Date(row.date);
      return paymentDate <= currentDate;
    });
    
    const futurePayments = schedule.filter(row => {
      const paymentDate = new Date(row.date);
      return paymentDate > currentDate;
    });
    
    // Show latest 3 historical payments + all future payments
    const latestHistorical = historicalPayments.slice(-3);
    return [...latestHistorical, ...futurePayments];
  };

  const visibleSchedule = getVisibleSchedule();
  
  // Check if there are more than 3 historical payments to determine if toggle is needed
  const currentDate = new Date();
  const historicalPayments = schedule.filter(row => {
    const paymentDate = new Date(row.date);
    return paymentDate <= currentDate;
  });
  const hasHistoricalData = historicalPayments.length > 3;

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      </Container>
    );
  }

  if (loans.length === 0) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ mt: 4 }}>
          <Typography variant="h4" gutterBottom>
            <ChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Loan Amortization Schedule
          </Typography>
          <Alert severity="info">No loans available. Add a loan to see the amortization schedule.</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ChartIcon />
          Loan Amortization Schedule
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          View detailed monthly breakdown of principal and interest payments
        </Typography>

        {/* Loan Selector */}
        <Box sx={{ mt: 3, mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Select Loan</InputLabel>
            <Select
              value={selectedLoanId || ''}
              onChange={(e) => setSelectedLoanId(Number(e.target.value))}
              label="Select Loan"
            >
              {loans.map((loan) => (
                <MenuItem key={loan.id} value={loan.id}>
                  {loan.loan_name} - {loan.lender} ({formatCurrency(loan.current_balance)})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {selectedLoan && schedule.length > 0 && (
          <>
            {/* Loan Summary */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {selectedLoan.loan_name} Summary
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Original Amount</Typography>
                    <Typography variant="h6">{formatCurrency(selectedLoan.original_amount)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Current Balance</Typography>
                    <Typography variant="h6">{formatCurrency(selectedLoan.current_balance)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Interest Rate</Typography>
                    <Typography variant="h6">
                      {selectedLoan.is_tracker 
                        ? `${selectedLoan.interest_rate.toFixed(2)}% (Tracker: ECB + ${selectedLoan.marginal_rate?.toFixed(2) ?? '0.00'}%)`
                        : `${selectedLoan.interest_rate.toFixed(2)}%`
                      }
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Monthly Payment</Typography>
                    <Typography variant="h6">{formatCurrency(selectedLoan.monthly_payment)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Payment Day</Typography>
                    <Typography variant="h6">
                      {selectedLoan.payment_day ? `${selectedLoan.payment_day}${getOrdinalSuffix(selectedLoan.payment_day)}` : 'Not Set'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Total Interest</Typography>
                    <Typography variant="h6" color="error">{formatCurrency(totalInterest)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Total Principal</Typography>
                    <Typography variant="h6" color="success.main">{formatCurrency(totalPrincipal)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Months Paid</Typography>
                    <Typography variant="h6">{historicalMonths} / {schedule.length}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">End Date</Typography>
                    <Typography variant="h6">{new Date(selectedLoan.end_date).toLocaleDateString('en-IE')}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Capital/Interest Chart */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Capital vs Interest Over Time
                </Typography>
                <Box sx={{ width: '100%', height: 400 }}>
                  {chartData.length > 0 ? (
                    <ResponsiveContainer>
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="year" 
                          angle={-45}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis 
                          tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          formatter={(value: number, name: string) => [
                            formatCurrency(value), 
                            name === 'Capital' ? 'Principal/Capital' : name
                          ]}
                          labelStyle={{ color: '#000' }}
                        />
                        <Legend />
                        <Bar dataKey="Capital" fill="#1a237e" name="Principal/Capital" stackId="a" />
                        <Bar dataKey="Interest" fill="#1976d2" name="Interest" stackId="a" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '100%',
                      backgroundColor: '#f5f5f5',
                      border: '1px dashed #ccc'
                    }}>
                      <Typography variant="body1" color="text.secondary">
                        {schedule.length === 0 ? 'No amortization data available' : 'Generating chart data...'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Amortization Schedule Table */}
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Monthly Breakdown
                  </Typography>
                  {hasHistoricalData && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setShowHistoricalMonths(!showHistoricalMonths)}
                      startIcon={showHistoricalMonths ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    >
                      {showHistoricalMonths ? 'Hide Historical' : `Show All Historical (${historicalPayments.length})`}
                    </Button>
                  )}
                </Box>
                <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
                  <Table stickyHeader size="small" sx={FIT_TABLE_SX}>
                    <FitColgroup
                      cols={equalFitCols(
                        ['month', 'date', 'interestRate', 'payment', 'principal', 'interest', 'balance', 'status'],
                        { flexKey: 'interestRate' }
                      )}
                    />
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Month</strong></TableCell>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell align="right"><strong>Interest Rate</strong></TableCell>
                        <TableCell align="right"><strong>Payment</strong></TableCell>
                        <TableCell align="right"><strong>Principal</strong></TableCell>
                        <TableCell align="right"><strong>Interest</strong></TableCell>
                        <TableCell align="right"><strong>Balance</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleSchedule.map((row) => (
                        <TableRow 
                          key={row.month}
                          sx={{
                            backgroundColor: row.isHistorical ? 'action.hover' : 'background.paper',
                            '&:hover': { backgroundColor: 'action.selected' }
                          }}
                        >
                          <TableCell>{row.month}</TableCell>
                          <TableCell>{row.date}</TableCell>
                          <TableCell align="right">
                            {selectedLoan?.is_tracker && row.interest_rate !== undefined ? (
                              <Box>
                                <Typography variant="body2">
                                  {formatPercentage(row.interest_rate)}
                                </Typography>
                                {selectedLoan.marginal_rate !== undefined && (
                                  <Typography variant="caption" color="text.secondary">
                                    (ECB: {formatPercentage((row.interest_rate - selectedLoan.marginal_rate))})
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              formatPercentage(row.interest_rate)
                            )}
                          </TableCell>
                          <TableCell align="right">{formatCurrency(row.payment)}</TableCell>
                          <TableCell align="right" sx={{ color: 'success.main' }}>
                            {formatCurrency(row.principal)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'error.main' }}>
                            {formatCurrency(row.interest)}
                          </TableCell>
                          <TableCell align="right"><strong>{formatCurrency(row.balance)}</strong></TableCell>
                          <TableCell>
                            {row.isHistorical ? (
                              <Chip label="Paid" size="small" color="success" sx={fitChipSx} />
                            ) : (
                              <Chip label="Future" size="small" color="default" sx={fitChipSx} />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </>
        )}
      </Box>
    </Container>
  );
};

